import { SolidDecision } from './bulkImportSolidsSceneState';

export type ManualReviewBlock =
  | 'no_action'
  | 'no_node_selected'
  | 'node_already_stope'
  | 'node_already_used'
  | 'no_source_node'
  | 'no_node_position'
  | 'no_pipe_type'
  | 'no_spool_selected'
  | 'no_existing_stope';

export type ManualReviewWarning = 'source_node_is_stope';

export interface ManualReviewContext {
  isStopeNode: (nodeId: string) => boolean;
  claimedNodeIds: Set<string>;
}

/** A node carries at most one stope, so two decisions on the same one would silently add a second. */
export const collectClaimedNodeIds = (
  decisions: Record<string, SolidDecision>,
  exceptEntityGuid: string | undefined,
): Set<string> => Object.values(decisions).reduce<Set<string>>((claimed, decision) => {
  if (decision.entityGuid === exceptEntityGuid) return claimed;
  if (decision.importMode === 'skip' || decision.action !== 'mark_as_stope') return claimed;
  if (decision.existingNodeId) claimed.add(decision.existingNodeId);
  return claimed;
}, new Set<string>());

/**
 * A projected stope with no source node is the one that matters: the import creates the node and the
 * stope but no spool, so the stope lands in the model connected to nothing.
 */
export const blockingReason = (
  decision: SolidDecision | undefined,
  context: ManualReviewContext,
): ManualReviewBlock | undefined => {
  if (!decision) return 'no_action';
  if (decision.importMode === 'skip') return undefined;
  if (decision.importMode === 'update_shapefile') {
    return decision.existingStopeId ? undefined : 'no_existing_stope';
  }

  if (decision.action === 'mark_as_stope') {
    const { existingNodeId } = decision;
    if (!existingNodeId) return 'no_node_selected';
    if (context.isStopeNode(existingNodeId)) return 'node_already_stope';
    if (context.claimedNodeIds.has(existingNodeId)) return 'node_already_used';
    return undefined;
  }

  if (decision.action === 'project_to_stope') {
    if (!decision.spoolFromNodeId) return 'no_source_node';
    if (!decision.newNodePosition) return 'no_node_position';
    if (!decision.spoolPipeTypeId) return 'no_pipe_type';
    return undefined;
  }

  if (decision.action === 'insert_between_nodes') {
    if (!decision.insertBetweenNodes) return 'no_spool_selected';
    if (!decision.newNodePosition) return 'no_node_position';
    return undefined;
  }

  return 'no_action';
};

/** Non-blocking: a spool running out of a stope node is legal but is almost always a mis-click. */
export const sourceNodeWarning = (
  decision: SolidDecision | undefined,
  context: ManualReviewContext,
): ManualReviewWarning | undefined => {
  if (decision?.action !== 'project_to_stope' || !decision.spoolFromNodeId) return undefined;
  return context.isStopeNode(decision.spoolFromNodeId) ? 'source_node_is_stope' : undefined;
};
